import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Paper,
  Text,
  Title,
  ActionIcon,
  Input,
  Divider,
  Group,
  Stack,
  Select,
  Center,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import useProfileStore from "../../store/useProfileStore";
import { IconPlus } from "../../shared/svg/AddIcon";
import { ClearIcon } from "../../shared/svg/ClearIcon";
import { SkillsEdit } from "../../types/profile-editing";
import LanguagesEditingWidget from "./LanguageEditing";
import {
  addSkills,
  deleteSkills,
  updateSkills,
} from "../../api/profileEditingApi";
import { EditIconSmall } from "../../shared/svg/EditIconSmall";
import { TrashCanSmall } from "../../shared/svg/TrashCanSmall";
import { notifications } from "@mantine/notifications";
import { CheckIcon } from "../../shared/svg/CheckIcon";
import { useMediaQuery } from "@mantine/hooks";
import ConfirmDelete from "../../utils/components/ConfirmDelete";

type SkillInputFormProps = {
  skillName: string;
  proficiency: string;
  experience?: number;
  onChangeName: (val: string) => void;
  onChangeProficiency: (val: string) => void;
  onChangeExperience: (val: number | undefined) => void;
  onCancel: () => void;
  onSubmit: () => void;
  edit: boolean;
  errorMessage: string | null;
  setErrorMessage: (e: string | null) => void;
};

const SkillInputForm = ({
  skillName,
  proficiency,
  experience,
  onChangeName,
  onChangeProficiency,
  onChangeExperience,
  onCancel,
  onSubmit,
  edit,
  errorMessage,
  setErrorMessage,
}: SkillInputFormProps) => {
  const { t } = useTranslation();
  const PROFICIENCY_OPTIONS = [
    { value: "not_specified", label: t("not_specified") },
    { value: "beginner", label: t("beginner") },
    { value: "intermediate", label: t("intermediate") },
    { value: "advanced", label: t("advanced") },
    { value: "expert", label: t("expert") },
  ];
  const matches = useMediaQuery("(max-width: 840px)");

  return (
    <Paper
      withBorder
      radius="md"
      p="md"
      style={{
        backgroundColor: "#fff",
        minWidth: 220,

        boxShadow: "0 1px 5px rgba(0,0,0,0.1)",
      }}
    >
      <Stack gap={8}>
        <Input
          value={skillName}
          onChange={(e) => {
            onChangeName(e.currentTarget.value);
            setErrorMessage(null);
          }}
          placeholder={t("enter_skill")}
          autoFocus
          size="sm"
        />

        <Group
          style={{ flexDirection: matches ? "column" : "row" }}
          grow={matches ? false : true}
          gap="sm"
          w="100%"
        >
          <Select
            value={proficiency}
            onChange={(value) => {
              onChangeProficiency(value || "not_specified");
              setErrorMessage(null);
            }}
            placeholder={t("enter_proficiency")}
            data={PROFICIENCY_OPTIONS}
            size="sm"
            w="100%"
          />

          <Input
            type="number"
            value={experience !== undefined ? experience : ""}
            onChange={(e) => {
              const val = parseInt(e.currentTarget.value, 10);
              onChangeExperience(isNaN(val) ? undefined : val);
            }}
            placeholder={t("enter_experience")}
            min={0}
            size="sm"
            w="100%"
          />
        </Group>

        {errorMessage && (
          <Text color="#B00020" size="xs" mt={4}>
            {errorMessage}
          </Text>
        )}

        <Group justify="space-between" mt={4}>
          <Button size="xs" onClick={onSubmit} disabled={!skillName.trim()}>
            {edit ? t("save") : t("add")}
          </Button>
          <ActionIcon
            color="gray"
            variant="subtle"
            onClick={onCancel}
            title={t("cancel")}
          >
            <ClearIcon />
          </ActionIcon>
        </Group>
      </Stack>
    </Paper>
  );
};

interface Props {
  setChanges: (t: boolean) => void;
}

const SkillsEditingWidget: React.FC<Props> = ({ setChanges }) => {
  const { t } = useTranslation();
  const { setUserMarketChanges, profile, fetchProfile } = useProfileStore();
  const [showInput, setShowInput] = useState(false);
  const [skills, setSkills] = useState<SkillsEdit[]>([]);

  const hardSkills = skills.filter((s) => s.type === "hard_skill");
  const softSkills = skills.filter((s) => s.type === "soft_skill");

  const [addingType, setAddingType] = useState<
    "hard_skill" | "soft_skill" | null
  >(null);

  const [editingSkill, setEditingSkill] = useState<SkillsEdit | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Поля форми
  const [skillName, setSkillName] = useState("");
  const [proficiency, setProficiency] = useState("not_specified");
  const [experience, setExperience] = useState<number | undefined>(undefined);
  const matches = useMediaQuery("(max-width: 650px)");

  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  useEffect(() => {
    if (profile?.skills.length) setSkills(profile?.skills);
  }, [profile]);

  useEffect(() => {
    fetchProfile();
  }, []);

  const PROFICIENCY_OPTIONS = [
    { value: "not_specified", label: t("not_specified") },
    { value: "beginner", label: t("beginner") },
    { value: "intermediate", label: t("intermediate") },
    { value: "advanced", label: t("advanced") },
    { value: "expert", label: t("expert") },
  ];

  const getProficiencyLabel = (value: string): string | undefined => {
    const option = PROFICIENCY_OPTIONS.find((opt) => opt.value === value);
    return option?.label;
  };

  const handleAddSkill = async () => {
    if (!skillName.trim()) return;
    const newSkill: SkillsEdit = {
      id: Date.now(),
      skill_name: skillName.trim(),
      proficiency: proficiency.trim() || undefined,
      years_of_experience: experience,
      type: addingType ? addingType : undefined,
    };
    try {
      const addedSkill = await addSkills(newSkill); // Викликаємо API
      setSkills([addedSkill, ...skills]);
      notifications.show({
        title: t("success"),
        message: t("add_successfully"),
        icon: <CheckIcon />,
        color: "green",
        style: {
          border: "1px solid #d3d3d3",
          borderRadius: "8px",
        },
      });
      setChanges(true);
      setUserMarketChanges(true);
      setSkillName("");
      setProficiency("not_specified");
      setExperience(undefined);
      setShowInput(false);
      setAddingType(null);
    } catch (error: any) {
      if (error.response.data.errors.non_field_errors) {
        setErrorMessage(error.response.data.errors.non_field_errors[0]);
      } else if (error.response.data.errors.skill_name) {
        setErrorMessage(error.response.data.errors.skill_name[0]);
      } else {
        setErrorMessage(t("somethingWentWrong"));
      }
    }
  };

  const handleRemoveSkill = async (id: number) => {
    notifications.show({
      title: t("success"),
      message: t("delete_successfully"),
      icon: <TrashCanSmall />,
      color: "#d3d3d3",
      style: {
        border: "1px solid #d3d3d3",
        borderRadius: "8px",
      },
    });
    setSkills((prev) => prev.filter((skill) => skill.id !== id));

    setChanges(true);
    setUserMarketChanges(true);
    if (id) {
      try {
        await deleteSkills(id);
      } catch (error: any) {
        if (error.response?.data?.errors?.non_field_errors) {
          setErrorMessage(error.response.data.errors.non_field_errors[0]);
        } else {
          setErrorMessage(t("somethingWentWrong"));
        }
      }
    }
  };

  const handleEditSkill = async () => {
    if (editingSkill) {
      try {
        const updated = await updateSkills({
          id: editingSkill.id,
          skill_name: skillName,
          proficiency: proficiency,
          years_of_experience: experience,
        });
        setSkills(
          skills.map((skill) =>
            skill.id === editingSkill.id ? updated : skill
          )
        );
        notifications.show({
          title: t("success"),
          message: t("changed_successfully"),
          icon: <EditIconSmall />,
          color: "#CEB7FF",
          style: {
            border: "1px solid #d3d3d3",
            borderRadius: "8px",
          },
        });
        setChanges(true);
        setUserMarketChanges(true);
        setEditingSkill(null);
        setSkillName("");
        setProficiency("not_specified");
        setExperience(undefined);
      } catch (error: any) {
        if (error.response.data.errors.non_field_errors) {
          setErrorMessage(error.response.data.errors.non_field_errors[0]);
        } else if (error.response.data.errors.skill_name) {
          setErrorMessage(error.response.data.errors.skill_name[0]);
        } else {
          setErrorMessage(t("somethingWentWrong"));
        }
      }
    }
  };

  const renderInputForm = () => (
    <Box style={{ width: "calc(33% - 20px)", minWidth: 220 }}>
      <SkillInputForm
        skillName={skillName}
        proficiency={proficiency}
        experience={experience}
        onChangeName={setSkillName}
        onChangeProficiency={setProficiency}
        onChangeExperience={setExperience}
        onCancel={() => {
          setShowInput(false);
          setSkillName("");
          setProficiency("not_specified");
          setExperience(undefined);
          setErrorMessage(null);
          setAddingType(null);
        }}
        errorMessage={errorMessage}
        setErrorMessage={setErrorMessage}
        onSubmit={handleAddSkill}
        edit={false}
      />
    </Box>
  );

  const renderSkill = (skill: SkillsEdit, index: number) => {
    const cardStyle = {
      backgroundColor: "rgb(245, 243, 250)",
      width: matches ? "100%" : "calc(33% - 14px)", // 4 картки на ряд (з урахуванням gap)
      minWidth: 220,
    };

    if (editingSkill?.id === skill.id) {
      return (
        <Box key={skill.id} style={cardStyle}>
          <SkillInputForm
            skillName={skillName}
            proficiency={proficiency}
            experience={experience}
            onChangeName={setSkillName}
            onChangeProficiency={setProficiency}
            onChangeExperience={setExperience}
            onCancel={() => {
              setEditingSkill(null);
              setSkillName("");
              setProficiency("not_specified");
              setExperience(undefined);
              setAddingType(null);
              setErrorMessage(null);
            }}
            errorMessage={errorMessage}
            setErrorMessage={setErrorMessage}
            onSubmit={handleEditSkill}
            edit={true}
          />
        </Box>
      );
    } else {
      return (
        <Paper
          key={skill.id}
          radius="md"
          withBorder
          p="xl"
          style={cardStyle}
          pos="relative"
        >
          <ActionIcon
            variant="subtle"
            color="gray"
            bg="transparent"
            onClick={() => {
              setEditingSkill(skill);
              setSkillName(skill.skill_name);
              setProficiency(skill.proficiency || "not_specified");
              setExperience(skill.years_of_experience || undefined);
              setErrorMessage(null);
            }}
            disabled={editingSkill !== null}
            style={{
              position: "absolute",
              top: 8,
              right: 38,
              backgroundColor: "#f2f2f2",
              touchAction: "manipulation",
              pointerEvents: "auto",
              zIndex: 10,
            }}
          >
            <EditIconSmall />
          </ActionIcon>
          <ActionIcon
            variant="subtle"
            color="gray"
            bg="transparent"
            onClick={() => {
              if (skill.id) {
                setIsDeleteOpen(true);
                setDeleteId(skill.id);
              }
              setErrorMessage(null);
            }}
            style={{
              position: "absolute",
              top: 8,
              right: 8,
              backgroundColor: "#f2f2f2",
              touchAction: "manipulation",
              pointerEvents: "auto",
              zIndex: 10,
            }}
          >
            <ClearIcon />
          </ActionIcon>
          <Group
            justify="space-between"
            align="flex-start"
            style={{ flexWrap: "nowrap" }}
            h="auto"
          >
            <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
              <Text fw={700} size="md" lineClamp={1}>
                {skill.skill_name}
              </Text>

              <Text size="sm" maw={250} style={{ whiteSpace: "normal" }}>
                {t("proficiency")}:{" "}
                {skill.proficiency
                  ? getProficiencyLabel(skill.proficiency)
                  : t("info_missing")}
              </Text>

              <Text
                style={{
                  wordBreak: "break-word",
                  overflowWrap: "break-word",
                  whiteSpace: "pre-wrap",
                }}
                size="sm"
              >
                {t("experience")}:{" "}
                {skill.years_of_experience
                  ? `${skill.years_of_experience} ${
                      skill.years_of_experience === 1 ? t("yearn") : t("years")
                    }`
                  : t("info_missing")}
              </Text>
            </Stack>
          </Group>
        </Paper>
      );
    }
  };

  return (
    <Box>
      <Flex justify="space-between" align="center" mb="30px">
        <Title size={32}>{t("your_skills")}</Title>
      </Flex>

      {skills !== undefined || showInput || editingSkill ? (
        <>
          <Box mb={40}>
            <Flex justify="space-between" gap={10} align="center" mb="md">
              <Title size={20}>{t("hard_skills")}</Title>
              <Button
                leftSection={<IconPlus />}
                onClick={() => {
                  setShowInput(true);
                  setAddingType("hard_skill");
                }}
                style={{ flexShrink: 0 }}
                disabled={showInput}
                variant="light"
                radius="md"
              >
                {!matches ? t("add_skill") : t("add")}
              </Button>
            </Flex>

            <Flex wrap="wrap" gap={20}>
              {addingType === "hard_skill" && showInput && renderInputForm()}
              {hardSkills.map(renderSkill)}
            </Flex>
          </Box>

          <Box>
            <Flex justify="space-between" align="center" gap={10} mb="md">
              <Title size={20}>{t("soft_skills")}</Title>
              <Button
                leftSection={<IconPlus />}
                onClick={() => {
                  setShowInput(true);
                  setAddingType("soft_skill");
                }}
                style={{ flexShrink: 0 }}
                disabled={showInput}
                variant="light"
                radius="md"
              >
                {!matches ? t("add_skill") : t("add")}
              </Button>
            </Flex>

            <Flex wrap="wrap" gap={20}>
              {addingType === "soft_skill" && showInput && renderInputForm()}
              {softSkills.map(renderSkill)}
            </Flex>
          </Box>
        </>
      ) : (
        <Center py="xl">
          <Paper
            radius="md"
            p="xl"
            withBorder
            shadow="sm"
            style={{ maxWidth: 1000, width: "100%" }}
          >
            <Flex
              align="center"
              w="100%"
              px={20}
              gap={matches ? 20 : 40}
              direction={matches ? "column" : "row"}
            >
              <Title
                order={2}
                fw={700}
                w={matches ? "100%" : "33%"}
                ta="left"
                style={{ fontSize: "1.5rem" }}
              >
                {t("nothing_here")}
              </Title>
              <Text
                size="xl"
                c="dimmed"
                w={matches ? "100%" : "66%"}
                style={{ fontSize: "1.125rem" }}
              >
                {t("no_hobbies")}
              </Text>
            </Flex>
          </Paper>
        </Center>
      )}

      <ConfirmDelete
        isOpen={isDeleteOpen}
        onClose={() => {
          setIsDeleteOpen(false);
          setDeleteId(null);
        }}
        onDelete={() => {
          if (deleteId !== null) handleRemoveSkill(deleteId);
        }}
        title={t("delete_skill_title")}
      />

      <Divider my={40} color="gray.4" h={2} />
      <LanguagesEditingWidget setChanges={setChanges} />
    </Box>
  );
};

export default SkillsEditingWidget;
